import { useState } from 'react';
import { Grid, Stack, Typography, TextField, IconButton, Divider, Box, Collapse } from '@mui/material';
import MainCard from 'components/MainCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import useAuth from 'hooks/useAuth';
import { Comment } from 'models/CommentModel';
import orderService from 'api/orderService';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { validateUserComment } from 'utils/userComments';
import { getLockOwnerName } from 'utils/orderLocking';

interface CommentProps {
  comments: Comment[];
  orderId: string;
  onAddComment: (text: string) => void;
  editLeaseId: string | null;
  onLockConflict?: (lockOwnerName: string) => void;
}

const Comments = ({ comments, orderId, onAddComment, editLeaseId, onLockConflict }: CommentProps) => {
  const [message, setMessage] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const { user } = useAuth();
  const hasEditLease = !!editLeaseId;

  const addComment = async () => {
    if (!editLeaseId) {
      throw new Error('Cannot add comment without an edit lease.');
    }

    await orderService.postComment(orderId, user?.userId!, message, editLeaseId);
    onAddComment(message);
  };

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    try {
      setSubmitError(null);
      await addComment();
      setMessage('');
    } catch (error) {
      const lockOwnerName = getLockOwnerName(error);

      if (lockOwnerName) {
        onLockConflict?.(lockOwnerName);
        return;
      }

      console.error('Failed to add comment:', error);
      setSubmitError('Failed to add comment. Please try again.');
    }
  };

  const validationError = validateUserComment(message);
  const helperText = validationError ?? submitError;

  const commentTitle = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        cursor: 'pointer'
      }}
      onClick={() => setExpanded((prev) => !prev)}
    >
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Comments
      </Typography>
      <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
    </Box>
  );

  return (
    <MainCard
      title={commentTitle}
      sx={{
        '& .MuiCardContent-root': {
          padding: expanded ? 'inherit' : '0 !important',
          transition: 'padding 0.3s'
        }
      }}
    >
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={3} sx={{ p: 2 }}>
          {comments.map((comment, index) => (
            <Box key={index}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle1" color="textPrimary">
                      {comment.userName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {format(comment.timestamp, 'dd/MM/yyyy HH:mm', { locale: enGB })}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    {comment.text}
                  </Typography>
                </Grid>
              </Grid>
              {index < comments.length - 1 && <Divider sx={{ my: 2 }} />}
            </Box>
          ))}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                multiline
                placeholder="Your message..."
                variant="outlined"
                size="small"
                value={message}
                error={helperText != null}
                helperText={helperText}
                disabled={!hasEditLease}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setSubmitError(null);
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      type="submit"
                      color="primary"
                      disabled={!message.trim() || validationError != null || !hasEditLease}
                      edge="end"
                      size="small"
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </IconButton>
                  )
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </Collapse>
    </MainCard>
  );
};

export default Comments;
